import type { FC, ReactNode } from 'react'

import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react'
import { cn } from '@/lib/utils'

type ToastTone = 'success' | 'error' | 'info'

interface ToastItem {
    id: number
    title: string
    description?: string
    tone: ToastTone
}

interface ToastOptions {
    description?: string
    tone?: ToastTone
}

interface ToastContextValue {
    toast: (title: string, options?: ToastOptions) => void
}

interface ToastProviderProps {
    children: ReactNode
}

const ToastContext = createContext<ToastContextValue | null>(null)

const TONE_STYLES: Record<ToastTone, { icon: typeof CircleCheck, className: string }> = {
    success: { icon: CircleCheck, className: 'text-emerald-400' },
    error: { icon: CircleAlert, className: 'text-rose-400' },
    info: { icon: Info, className: 'text-sky-400' }
}

export const ToastProvider: FC<ToastProviderProps> = ({ children }): ReactNode => {
    const [items, setItems] = useState<Array<ToastItem>>([])
    const counter = useRef(0)

    const dismiss = useCallback((id: number) => {
        setItems(list => list.filter(item => item.id !== id))
    }, [])

    const toast = useCallback((title: string, options: ToastOptions = {}) => {
        counter.current += 1

        const id = counter.current

        setItems(list => [...list.slice(-2), { id, title, description: options.description, tone: options.tone ?? 'success' }])
        window.setTimeout(() => dismiss(id), options.tone === 'error' ? 7000 : 5200)
    }, [dismiss])

    const value = useMemo(() => ({ toast }), [toast])

    return (
        <ToastContext.Provider value={value}>
            {children}

            <div aria-live='polite' className='pointer-events-none fixed inset-x-4 bottom-4 z-[120] flex flex-col items-end gap-2 sm:left-auto sm:right-6 sm:bottom-6'>
                <AnimatePresence initial={false}>
                    {items.map(item => {
                        const Icon = TONE_STYLES[item.tone].icon

                        return (
                            <motion.div
                                key={item.id}
                                layout
                                initial={{ opacity: 0, y: 16, scale: 0.96 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, x: 40, scale: 0.96 }}
                                transition={{ type: 'spring', stiffness: 420, damping: 32 }}
                                role={item.tone === 'error' ? 'alert' : 'status'}
                                className='pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-2xl border border-white/10 bg-midnight/95 px-4 py-3.5 text-white shadow-[0_24px_60px_-20px_rgba(8,18,30,0.7)] backdrop-blur-xl'
                            >
                                <Icon className={cn('mt-0.5 size-5 shrink-0', TONE_STYLES[item.tone].className)} />

                                <div className='min-w-0 flex-1'>
                                    <p className='text-sm font-semibold leading-snug'>
                                        {item.title}
                                    </p>

                                    {item.description && (
                                        <p className='mt-1 text-[13px] leading-relaxed text-slate-300'>
                                            {item.description}
                                        </p>
                                    )}
                                </div>

                                <button
                                    type='button'
                                    onClick={() => dismiss(item.id)}
                                    aria-label='Dismiss notification'
                                    className='-mr-1 rounded-md p-1 text-slate-400 transition hover:bg-white/10 hover:text-white'
                                >
                                    <X className='size-4' />
                                </button>
                            </motion.div>
                        )
                    })}
                </AnimatePresence>
            </div>
        </ToastContext.Provider>
    )
}

export const useToast = (): ToastContextValue => {
    const context = useContext(ToastContext)

    if (!context) throw new Error('useToast must be used inside ToastProvider.')

    return context
}
